import {
  FilterId,
  type PrimaryFilterId,
} from '../../../../filters/types/filters';
import { getPrimaryDimension } from '../../../ChartConfig/utils/table-columns';
import type { SecondaryDimensionValues } from '../../../PageConfigProvider/types/dimension';
import type { TableColumnConfig } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';

export const primaryDimensionFromFilterId = (
  primaryFilterId: PrimaryFilterId
): SecondaryDimensionValues => {
  switch (primaryFilterId) {
    case FilterId.COMPANY:
      return 'company';
    case FilterId.GEOGRAPHY:
      return 'geography';
    case FilterId.ROLE:
      return 'role';
  }
};

type TableQueryInputs = {
  dimensionSource?: SecondaryDimensionValues;
  isDimensionSourcePrimary?: boolean;
  secondaryDimension?: SecondaryDimensionValues;
  metrics: MetricValues[];
};

export const deriveQueryFromColumns = (
  columns: TableColumnConfig[] | undefined,
  primaryFilterId: PrimaryFilterId | undefined
): TableQueryInputs => {
  const cols = columns ?? [];

  const { dimensionSource, isDimensionSourcePrimary, secondaryDimension } =
    getDimensionColumns({ cols, primaryFilterId });

  const metrics: MetricValues[] = [];
  for (const c of cols) {
    if (c.kind === 'variable' && !metrics.includes(c.metric)) {
      metrics.push(c.metric);
    }
  }

  return {
    dimensionSource,
    isDimensionSourcePrimary,
    secondaryDimension,
    metrics,
  };
};

const supportedDimensionSources: SecondaryDimensionValues[] = [
  'company',
  'geography',
  'role',
  'rics',
  'skills',
  'seniority',
  'education',
  'ethnicity',
  'gender',
  'competitors',
];
/** Get supported dimension columns
 * Right now, tables support a maximum of 2 dimension columns
 *
 * The primary dimension should always default to the page's primary unit of analysis if it is defined as a column
 * in the table. If not, the first supported primary dimension should be used.
 *
 * The first additional dimension column will be used
 * as the secondary dimension.
 */
const getDimensionColumns = ({
  cols,
  primaryFilterId,
}: {
  cols: TableColumnConfig[];
  primaryFilterId: PrimaryFilterId | undefined;
}) => {
  const dimensionColumns = cols.filter((c) => c.kind === 'dimension');

  let isDimensionSourcePrimary = false;
  const dimensionSource = (() => {
    const primaryDimension =
      primaryFilterId &&
      dimensionColumns.find(
        (c) => c.dimension === getPrimaryDimension(primaryFilterId)
      )?.dimension;
    if (primaryDimension) {
      isDimensionSourcePrimary = true;
      return primaryDimension;
    }
    const firstDimensionSource = supportedDimensionSources.find((d) =>
      dimensionColumns.some((c) => c.dimension === d)
    );
    if (firstDimensionSource) return firstDimensionSource;
    return undefined;
  })();
  const secondaryDimension = dimensionColumns.filter(
    (c) => c.dimension !== dimensionSource
  )?.[0]?.dimension;

  return { dimensionSource, isDimensionSourcePrimary, secondaryDimension };
};
